import { Component, Input, ElementRef, HostListener, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ContentStoreService, MxLoggerService, AuthStoreService, NavMenu } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs/operators';
import { User } from 'src/app/core/model/user';
import { UserStoreService } from 'src/app/core/store/user-store.service';
import { environment } from 'src/environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @Input() hideHeaderMenu = false;
  @ViewChild('header', { static: true }) headerElement: ElementRef<HTMLElement>;

  logoUrl =
    'https://mmicdn.s3.eu-central-1.amazonaws.com/Lenovo%2BElevate/Images/Lenovo_360_Elevate_logo_bold_Black_RGB.png';

  headerFixed = environment.headerFixed;

  authenticatedUser$ = this.authStore.authenticatedUser$.pipe(untilDestroyed(this));
  userRole: string;
  isDistributor = false;
  isIsg = false;
  isIdg = false;
  isCountryOnCreditNotePaymentType = false;
  invoiceUploadFeatureIsOpen = false;
  invoiceUploadExtendedPeriodOpen = false;
  acceptedInPreviousQuarter = true;
  isPastMember = false;
  hideHeaderMenuItems = false;

  headerMenu: NavMenu;

  currencySymbol$ = this.userStore.currency$.pipe(
    filter(Boolean),
    map((currency) => currency.symbol)
  );

  constructor(
    private authStore: AuthStoreService,
    private renderer: Renderer2,
    private contentStore: ContentStoreService,
    private mxLogger: MxLoggerService,
    private userStore: UserStoreService
  ) {}

  ngOnInit(): void {
    this.contentStore.headerMenu$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((headerMenu) => {
      this.mxLogger.debug('HeaderComponent', 'ngOnInit() headerMenu=', headerMenu);

      this.resetHeaderMenu();
    });

    this.authStore.userSettings$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((userSettings: any) => {
      this.userRole = (userSettings?.user as User)?.userRole?.name ?? '';
      this.isDistributor = this.userRole === 'lenovo.elevate.disti.access';

      const companies = (this.authStore.userSettings as any)?.user?.companies;
      this.hideHeaderMenuItems = (userSettings?.user as User)?.acceptedTermsConditions === false;

      if (companies && companies.length > 0) {
        this.isIsg = companies[0].elevateCompanyDetails?.isIsg === true;
        this.isIdg = companies[0].elevateCompanyDetails?.isPcsd === true;
      }

      this.resetHeaderMenu();
    });

    this.userStore.invoiceDetailsQuarters$
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((invoiceDetailsQuarters) => {
        this.isCountryOnCreditNotePaymentType = invoiceDetailsQuarters?.isCountryOnCreditNotePaymentType;
        this.invoiceUploadFeatureIsOpen = invoiceDetailsQuarters?.invoiceUploadFeatureIsOpen === true;
        this.invoiceUploadExtendedPeriodOpen = invoiceDetailsQuarters?.invoiceUploadExtendedPeriodOpen === true;

        this.resetHeaderMenu();
      });

    this.authStore.isRegularUser$.pipe(untilDestroyed(this), filter(Boolean)).subscribe(() => {
      const userRole = (this.authStore.userSettings?.user as User)?.userRole;
      this.isPastMember = userRole?.name?.includes('past') ?? false;
      this.resetHeaderMenu();
    });

    this.userStore.rebateSummaryDetails$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((rebateSummaries) => {
      const previousQuarterRebateSum = rebateSummaries.find((r) => r.yrQtr === this.userStore.getPreviousQuarter());

      if (
        !previousQuarterRebateSum ||
        previousQuarterRebateSum.acceptanceStatus !== 1 ||
        !this.isValidDate(previousQuarterRebateSum.acceptanceDate)
      ) {
        this.acceptedInPreviousQuarter = false;
        this.resetHeaderMenu();
      }
    });
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (environment.headerFixed) {
      if (window.scrollY > 0) {
        this.renderer.addClass(this.headerElement.nativeElement, 'scrolled');
      } else {
        this.renderer.removeClass(this.headerElement.nativeElement, 'scrolled');
      }
    }
  }

  resetHeaderMenu(): void {
    if (this.contentStore.headerMenu) {
      const headerMenu: NavMenu = JSON.parse(JSON.stringify(this.contentStore.headerMenu));

      if (this.isCountryOnCreditNotePaymentType) {
        const menuIndex = headerMenu.items.findIndex((navItem) => navItem.url.includes('rebate-claim'));

        if (menuIndex >= 0) {
          headerMenu.items.splice(menuIndex, 1);
        }
      }

      const pastMemberCanClaimRebate =
        this.invoiceUploadExtendedPeriodOpen ||
        (this.invoiceUploadFeatureIsOpen && this.acceptedInPreviousQuarter !== false);

      if (this.isPastMember && !pastMemberCanClaimRebate) {
        const rebateMenuIndex = headerMenu.items.findIndex((navItem) => navItem.url.includes('rebate-claim'));

        if (rebateMenuIndex >= 0) {
          headerMenu.items.splice(rebateMenuIndex, 1);
        }
      }

      this.headerMenu = headerMenu;
    }
  }

  private isValidDate(value: any): boolean {
    if (value !== null && value !== undefined) {
      const d = new Date(value);
      return !Number.isNaN(d.getTime());
    }
    return false;
  }
}
